import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/shared/ui/hover-card";
import { Tooltip } from "@/shared/ui/tooltip";
import { mainNavigation } from "@/app/layouts/navigation";
import { cn } from "@/shared/lib/cn";
import { Link } from "@tanstack/react-router";
import { ChevronDown, Home } from "lucide-react";

interface HomeMenuItemProps {
  collapsed: boolean;
  onNavigate?: () => void;
}

export function HomeMenuItem({ collapsed, onNavigate }: HomeMenuItemProps) {
  const baseClasses =
    "group relative flex items-center gap-3 overflow-hidden rounded-2xl px-3 py-2 text-sm transition-all duration-200";
  const collapseAdjustment = collapsed ? "justify-center px-2" : "";

  const homeItem = (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Link
          to="/home"
          onClick={onNavigate}
          className={cn(
            baseClasses,
            "border border-transparent bg-white/5 font-medium text-white/80",
            "hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/10",
            "data-[status=active]:border-imperial-500/70 data-[status=active]:bg-imperial-500/20 data-[status=active]:text-white data-[status=active]:shadow-telemetry",
            collapseAdjustment
          )}
        >
          <span
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition duration-200",
              "group-hover:border-white/40 group-hover:bg-white/15 group-hover:text-white"
            )}
          >
            <Home aria-hidden="true" className="h-4 w-4" />
          </span>
          {!collapsed ? (
            <div className="flex flex-1 items-center justify-between">
              <span className="font-bold">Home</span>
              <ChevronDown
                aria-hidden="true"
                className="h-4 w-4 text-white/50 transition-transform group-hover:text-white/80"
              />
            </div>
          ) : (
            <span className="sr-only">Home</span>
          )}
          <span className="absolute inset-y-0 left-0 w-[3px] rounded-r-full bg-linear-to-b from-imperial-400 via-imperial-500 to-imperial-600 opacity-0 transition-opacity duration-200 group-data-[status=active]:opacity-100" />
        </Link>
      </HoverCardTrigger>
      <HoverCardContent side="right" className="w-64 p-2">
        <div className="space-y-1">
          {mainNavigation.map((item) => {
            const searchParams: Record<string, string | undefined> = {};
            if (item.target.search) {
              Object.entries(item.target.search).forEach(([key, value]) => {
                if (value !== undefined) {
                  searchParams[key] = String(value);
                }
              });
            }
            return (
              <Link
                key={item.id}
                to={item.target.to}
                params={item.target.params}
                search={searchParams}
                onClick={onNavigate}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );

  if (collapsed) {
    return (
      <Tooltip
        content="Home"
        description="Access operations menu"
        side="right"
        align="center"
        delayDuration={120}
      >
        {homeItem}
      </Tooltip>
    );
  }

  return homeItem;
}
